import { useEffect, useState, useCallback } from "react";
import { api, fmtNum, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Download, Save, Sparkles, Copy, Loader2, Info } from "lucide-react";

const FORMATS = ["jsonl", "json", "csv", "txt"];
const SHAPES = [
  { v: "text_meta", label: "text + meta (pretraining)" },
  { v: "instruction", label: "instruction / input / output" },
  { v: "fields", label: "raw fields (pick your own)" },
  { v: "knowledge", label: "knowledge (Empire → helm-llm Knowledge)" },
  { v: "training", label: "training messages (Empire, train/eval split)" },
];
const CHUNKS = [
  { v: 0, label: "No chunking — one record per page" },
  { v: 512, label: "512 tokens — small, embeddings / RAG" },
  { v: 1024, label: "1,024 tokens — RAG sweet spot" },
  { v: 2048, label: "2,048 tokens — recommended for fine-tuning" },
  { v: 4096, label: "4,096 tokens — long-form, 8k+ context models" },
  { v: 8192, label: "8,192 tokens — very long context only" },
];
const ALL_FIELDS = ["url", "title", "domain", "content", "tokens_est", "relevance_score",
  "scraped_at", "status_code", "content_length", "extraction_mode"];

const RECO = {
  0: "One record per page keeps documents whole — best for continued pretraining on long articles.",
  512: "Small chunks are ideal for embedding + retrieval, but too short for instruction tuning.",
  1024: "The most common size for RAG pipelines — enough context without blowing the window.",
  2048: "Best all-round choice: fits comfortably in 4k-context fine-tunes with room for the prompt.",
  4096: "Use only if your base model has 8k+ context, otherwise samples get truncated in training.",
  8192: "Very large samples — slow to train and easily truncated. Pick this only for long-context models.",
};

export default function ExportStudio({ crawlId, hasPages, onSaved }) {
  const [opts, setOpts] = useState({
    fmt: "jsonl", dedupe: true, shape: "text_meta", chunk_tokens: 2048,
    fields: ["url", "title", "domain", "content"],
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setOpts((o) => ({ ...o, [k]: v }));

  const loadPreview = useCallback(async () => {
    if (!crawlId || !hasPages) { setPreview(null); return; }
    try {
      setPreview(await api.previewExport(crawlId, opts));
    } catch (e) { setPreview(null); }
  }, [crawlId, hasPages, opts]);

  useEffect(() => {
    const t = setTimeout(loadPreview, 350);
    return () => clearTimeout(t);
  }, [loadPreview]);

  const download = async () => {
    if (!hasPages) { toast.error("No data to export yet"); return; }
    try {
      await downloadFile(api.exportUrl(crawlId, opts), `dataforge-${crawlId.slice(0, 8)}.${opts.fmt}`);
      toast.success(`Downloaded ${opts.fmt.toUpperCase()}`);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const save = async () => {
    if (!hasPages) { toast.error("No data to export yet"); return; }
    setBusy(true);
    try {
      const f = await api.saveExport(crawlId, opts);
      toast.success(`Saved ${f.filename} (${fmtNum(f.records)} records)`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save file");
    } finally { setBusy(false); }
  };

  const toggleField = (f) => setOpts((o) => ({
    ...o,
    fields: o.fields.includes(f) ? o.fields.filter((x) => x !== f) : [...o.fields, f],
  }));

  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] overflow-hidden" data-testid="export-studio">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
        <Download className="h-4 w-4 text-cyan-400" />
        <h3 className="font-display font-bold text-base">Export Dataset</h3>
        {preview && (
          <span data-testid="export-est-records" className="ml-auto font-mono text-[11px] text-cyan-400 tabular-nums">
            ≈ {fmtNum(preview.est_records)} records from {fmtNum(preview.pages)} pages
          </span>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 p-5">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Format</Label>
              <Select value={opts.fmt} onValueChange={(v) => set("fmt", v)}>
                <SelectTrigger data-testid="export-format-select" className="bg-[#0E1420] border-border text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMATS.map((f) => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Record shape</Label>
              <Select value={opts.shape} onValueChange={(v) => set("shape", v)}>
                <SelectTrigger data-testid="export-shape-select" className="bg-[#0E1420] border-border text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SHAPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {opts.shape === "training" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Split (eval = held-out real examples)</Label>
              <Select value={opts.split || "train"} onValueChange={(v) => set("split", v)}>
                <SelectTrigger data-testid="export-split-select" className="bg-[#0E1420] border-border text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["train", "eval", "all"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Chunk size</Label>
            <Select value={String(opts.chunk_tokens)} onValueChange={(v) => set("chunk_tokens", Number(v))}>
              <SelectTrigger data-testid="export-chunk-select" className="bg-[#0E1420] border-border text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHUNKS.map((c) => <SelectItem key={c.v} value={String(c.v)}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p data-testid="export-chunk-reco" className="text-[11px] text-slate-500 flex gap-1.5 leading-relaxed">
              <Info className="h-3 w-3 mt-0.5 shrink-0 text-cyan-500" />{RECO[opts.chunk_tokens]}
            </p>
          </div>

          <div className="flex items-center justify-between rounded-md border border-border bg-[#0E1420] px-3 py-2.5">
            <div>
              <Label className="text-xs text-slate-300">Strip duplicates</Label>
              <p className="text-[10px] font-mono text-slate-600 mt-0.5">Removes exact + near-identical pages</p>
            </div>
            <Switch data-testid="export-dedupe-switch" checked={opts.dedupe} onCheckedChange={(v) => set("dedupe", v)} />
          </div>

          {opts.shape === "fields" && (
            <div className="space-y-2" data-testid="export-fields-picker">
              <Label className="text-xs text-slate-400">Fields to include</Label>
              <div className="flex flex-wrap gap-1.5">
                {ALL_FIELDS.map((f) => (
                  <button key={f} data-testid={`export-field-${f}`} onClick={() => toggleField(f)}
                    className={cn("px-2 py-1 rounded font-mono text-[10px] border transition-colors",
                      opts.fields.includes(f)
                        ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300"
                        : "border-border text-slate-500 hover:text-slate-300")}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-1">
            <Button data-testid="export-download-button" onClick={download}
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
              <Download className="h-4 w-4 mr-1.5" /> Download
            </Button>
            <Button data-testid="export-save-button" onClick={save} disabled={busy} variant="outline"
              className="border-border hover:bg-emerald-500/10 hover:border-emerald-500/50 hover:text-emerald-300">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save to Files
            </Button>
          </div>
        </div>

        {/* Live preview */}
        <div className="rounded-md border border-border bg-[#04060A] overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Sparkles className="h-3.5 w-3.5 text-amber-400" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Record preview</span>
            {preview?.sample?.length > 0 && (
              <button data-testid="export-copy-preview"
                onClick={() => { navigator.clipboard?.writeText(JSON.stringify(preview.sample, null, 2)); toast.success("Preview copied"); }}
                className="ml-auto text-slate-500 hover:text-cyan-300">
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <pre data-testid="export-preview"
            className="terminal-scroll flex-1 max-h-[320px] min-h-[220px] overflow-auto p-3 font-mono text-[11px] text-cyan-200/90 leading-relaxed">
            {preview?.sample?.length
              ? preview.sample.map((r) => JSON.stringify(r, null, 2)).join("\n\n")
              : "// collect pages first to preview records"}
          </pre>
        </div>
      </div>
    </div>
  );
}
